import { Alert, Spinner } from 'flowbite-react';
import { useTranslation } from 'react-i18next';
import { MigrationProgress } from '../../lib/model/migration/migrationStreamApi.ts';

/**
 * Generic progress display for any migrator run started via migrationStreamApi's
 * useMigrateLazyQuery — shared by every section of Migration.tsx so each migrator only
 * has to own its own trigger button + params, not its own progress UI.
 *
 * Note there's no per-item success/failure distinction to show here: Migrator.migrate()
 * (backend) only ever emits MigratedItem{id, message} for completed steps — a failure
 * mid-run surfaces as the whole stream erroring out (status "error" below), not as a
 * failed item alongside successful ones. So "items" below are always things that
 * succeeded; if the run failed partway, the items already received are still shown,
 * plus the error.
 */
export default function MigrationProgressPanel({ progress }: { progress: MigrationProgress | undefined }) {
    const { t } = useTranslation("migration");

    if (progress === undefined) {
        return null;
    }

    const lastItem = progress.items[progress.items.length - 1];

    return (
        <div className="mt-2 flex flex-col gap-2">
            <div className="flex items-center gap-2">
                {progress.status === "running" && <Spinner size="sm"/>}
                <span>
                    {t("migration_status_" + progress.status)}
                    {" — "}
                    {t("migration_items_processed", { count: progress.items.length })}
                </span>
            </div>

            {lastItem && (
                <div className="text-sm text-gray-500">
                    {t("migration_last_message", { message: lastItem.message })}
                </div>
            )}

            {progress.status === "error" && (
                <Alert color="failure">
                    {t("migration_error_message", { message: progress.errorMessage ?? "" })}
                </Alert>
            )}

            {progress.items.length > 0 && (
                <ul className="max-h-40 overflow-y-auto text-sm text-gray-500">
                    {progress.items.map((item) => (
                        <li key={item.id}>{item.message}</li>
                    ))}
                </ul>
            )}
        </div>
    );
}
